import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import ExportDialog from "@/components/ExportDialog";
import { Loader2, Plus, MoreVertical, Pencil, Ban, CheckCircle2, Trash2, UploadCloud, DownloadCloud, UserCog, ClipboardList, CheckSquare, Search, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", username: "", password: "", role: "staff", staff_type: "Teaching", phone: "", email: "",
  designation: "", gender: "Male", dob: "", qualification: "", joining_date: "", address: "", salary: "",
  photo: "", staff_id: "", father_name: "", aadhar_number: "", aadhar_photo: "", spouse_name: "",
  marital_status: "Unmarried", caste: "", religion: "", category: "General", nationality: "Indian",
  last_organisation: "", experience: "", last_position: "",
  account_number: "", account_holder_name: "", ifsc_code: "", bank_name: "", branch_name: "",
  pan_no: "", pf_account_number: "",
  assigned_classes: [],
};

const RELIGIONS = ["Hindu", "Muslim", "Christian", "Buddhism", "Sikh"];
const CATEGORIES = ["General", "OBC", "SC", "ST", "EWS"];
const DEFAULT_ROLES = ["Principal", "Vice-Principal", "Administrator", "Academic Head", "Teacher"];

function ToolBtn({ icon: Icon, label, onClick, disabled, variant = "blue", testid }) {
  const colors = variant === "red" ? "bg-rose-500 hover:bg-rose-600" : "bg-sky-500 hover:bg-sky-600";
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} disabled={disabled} data-testid={testid}
      className={`h-10 w-10 rounded-xl text-white flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${colors}`}>
      <Icon size={18} />
    </button>
  );
}

const roleColor = { admin: "bg-violet-100 text-violet-700", accountant: "bg-blue-100 text-blue-700", staff: "bg-emerald-100 text-emerald-700" };

function PwdInput({ value, onChange, label, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div><Label className="text-xs">{label}</Label>
      <div className="mt-1 relative">
        <Input type={show ? "text" : "password"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="pr-9" data-testid={testid} />
        <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function LoginCell({ username, password }) {
  const [show, setShow] = useState(false);
  return (
    <div className="text-xs">
      <div className="text-slate-700 font-medium" data-testid={`staff-login-username-${username}`}>{username || "—"}</div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {password
          ? <span className="text-slate-500 tabular" data-testid={`staff-login-password-${username}`}>{show ? password : "••••••"}</span>
          : <span className="text-slate-400 italic">reset to view</span>}
        {password && <button type="button" onClick={() => setShow((v) => !v)} className="text-slate-400 hover:text-slate-600" data-testid={`staff-login-toggle-${username}`} aria-label={show ? "Hide password" : "Show password"}>{show ? <EyeOff size={13} /> : <Eye size={13} />}</button>}
      </div>
    </div>
  );
}

export function StaffForm({ open, onOpenChange, edit, onSaved, initial, addTitle, onAdded }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [classes, setClasses] = useState([]);
  const [classDlgOpen, setClassDlgOpen] = useState(false);
  useEffect(() => {
    if (open) {
      setF(edit ? { ...empty, ...edit, password: edit.password_plain || "", salary: edit.salary ?? "", assigned_classes: edit.assigned_classes || [] } : { ...empty, ...(initial || {}) });
      setAddRoleOpen(false); setNewRole("");
      api.get("/staff-roles").then((r) => setRoles(r.data.roles || DEFAULT_ROLES)).catch(() => {});
      api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    }
  }, [open, edit, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleClass = (id) => setF((p) => { const cur = p.assigned_classes || []; return { ...p, assigned_classes: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });

  const addRole = async () => {
    const name = newRole.trim();
    if (!name) { toast.error("Enter a role name"); return; }
    try {
      const r = await api.post("/staff-roles", { name });
      setRoles(r.data.roles || roles);
      set("designation", name);
      setNewRole(""); setAddRoleOpen(false);
      toast.success("Role added");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.username.trim()) { toast.error("Username is required"); return; }
    if (!edit && !f.password.trim()) { toast.error("Password is required"); return; }
    if (f.aadhar_number && !/^\d{12}$/.test(f.aadhar_number)) { toast.error("Aadhar number must be exactly 12 digits"); return; }
    setSaving(true);
    try {
      const payload = { ...f, salary: Number(f.salary) || 0 };
      if (edit) {
        if (!payload.password) delete payload.password;
        delete payload.password_plain;
        await api.put(`/staff/${edit.id}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/staff", payload);
        toast.success("Staff added");
        if (onAdded) await onAdded();
      }
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onPhoto = (key, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (key !== "photo") { set(key, reader.result); return; }
      const img = new Image();
      img.onload = () => {
        const size = 500;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        set(key, canvas.toDataURL("image/jpeg", 0.9));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const fRef = useRef(f); fRef.current = f;
  const setRef = useRef(set); setRef.current = set;
  const F = useCallback(({ k, label, type = "text" }) => (
    <div><Label className="text-xs">{label}</Label><Input type={type} value={(fRef.current || {})[k] ?? ""} onChange={(e) => setRef.current(k, e.target.value)} className="mt-1" data-testid={`staff-${k}`} /></div>
  ), []);
  const Sel = useCallback(({ k, label, options }) => (
    <div><Label className="text-xs">{label}</Label>
      <Select value={(fRef.current || {})[k] || ""} onValueChange={(v) => setRef.current(k, v)}>
        <SelectTrigger className="mt-1" data-testid={`staff-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  ), []);
  const PhotoUp = useCallback(({ k, label }) => (
    <div><Label className="text-xs">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center text-[9px] text-slate-400 shrink-0">
          {(fRef.current || {})[k] ? <img src={fRef.current[k]} alt={label} className="w-full h-full object-cover" /> : "None"}
        </div>
        <Input type="file" accept="image/*" onChange={(e) => onPhoto(k, e.target.files?.[0])} className="text-xs" data-testid={`staff-${k}`} />
      </div>
    </div>
  ), []);

  const Section = ({ title }) => <div className="col-span-full text-xs font-semibold uppercase tracking-wide text-indigo-500 border-b border-slate-100 pb-1 mt-2">{title}</div>;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle>{edit ? "Edit Staff" : (addTitle || "Add Staff")}</DialogTitle>
          <DialogDescription>Enter personal, professional and login details for this staff member.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Section title="Personal Details" />
          <F k="name" label="Full Name" />
          <F k="staff_id" label="Staff ID" />
          <F k="father_name" label="Father's Name" />
          <Sel k="role" label="Access Level" options={["staff", "accountant"]} />
          <Sel k="staff_type" label="Staff Type" options={["Teaching", "Non-Teaching"]} />
          <div className="col-span-2 md:col-span-1">
            <Label className="text-xs">Role</Label>
            <div className="mt-1 flex gap-1.5">
              <Select value={f.designation || ""} onValueChange={(v) => set("designation", v)}>
                <SelectTrigger className="flex-1" data-testid="staff-designation"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {[...new Set([...roles, ...(f.designation ? [f.designation] : [])])].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setAddRoleOpen((v) => !v)} data-testid="staff-add-role-btn"><Plus size={16} /></Button>
            </div>
            {addRoleOpen && (
              <div className="mt-1.5 flex gap-1.5">
                <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRole(); } }} placeholder="New role name" className="h-9" data-testid="staff-new-role-input" />
                <Button type="button" size="sm" className="shrink-0" onClick={addRole} data-testid="staff-save-role-btn">Add</Button>
              </div>
            )}
          </div>
          <Sel k="gender" label="Gender" options={["Male", "Female", "Other"]} />
          <F k="phone" label="Phone" />
          <F k="email" label="Email" type="email" />
          <div className="col-span-2">
            <Label className="text-xs">Assign Classes (Class Teacher)</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Button type="button" variant="outline" size="sm" onClick={() => setClassDlgOpen(true)} data-testid="staff-assign-classes-btn"><Plus size={14} className="mr-1" />Assign</Button>
              {(f.assigned_classes || []).length === 0
                ? <span className="text-xs text-slate-400">No classes assigned</span>
                : (f.assigned_classes || []).map((id) => { const c = classes.find((x) => x.id === id); return <span key={id} className="text-xs bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5" data-testid={`staff-assigned-chip-${id}`}>{c ? `${c.name}${c.section ? "-" + c.section : ""}` : id}</span>; })}
            </div>
          </div>
          <F k="dob" label="Date of Birth" type="date" />
          <Sel k="marital_status" label="Marital Status" options={["Married", "Unmarried"]} />
          <F k="spouse_name" label="Spouse Name" />
          <F k="nationality" label="Nationality" />
          <F k="caste" label="Caste" />
          <Sel k="religion" label="Religion" options={RELIGIONS} />
          <Sel k="category" label="Category" options={CATEGORIES} />
          <F k="aadhar_number" label="Aadhar No. (12 digits)" />
          <PhotoUp k="photo" label="Profile Photo" />
          <PhotoUp k="aadhar_photo" label="Aadhar Photo" />
          <div className="col-span-2"><F k="address" label="Address" /></div>

          <Section title="Login" />
          <F k="username" label="Login Username" />
          <PwdInput label="Login Password" value={f.password} onChange={(v) => set("password", v)} testid="staff-password" />
          {edit && <div className="col-span-full text-[11px] text-slate-400 -mt-1">Leave the password blank to keep the current one. Any new password you set is shown in the staff table's Login column.</div>}

          <Section title="Professional" />
          <F k="qualification" label="Qualification" />
          <F k="last_organisation" label="Last Organisation" />
          <F k="last_position" label="Last Position" />
          <F k="experience" label="Experience" />
          <F k="joining_date" label="Joining Date" type="date" />
          <F k="salary" label="Monthly Salary (₹)" type="number" />

          <Section title="Bank Details" />
          <F k="account_holder_name" label="Account Holder Name" />
          <F k="account_number" label="Account Number" />
          <F k="ifsc_code" label="IFSC Code" />
          <F k="bank_name" label="Bank Name" />
          <F k="branch_name" label="Branch Name" />
          <F k="pan_no" label="PAN No." />
          <F k="pf_account_number" label="PF Account Number" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-staff-btn">{saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}{edit ? "Save Changes" : "Save Staff"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={classDlgOpen} onOpenChange={setClassDlgOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Classes</DialogTitle>
          <DialogDescription>Select one or more classes to assign to this staff member. They will be set as the Class Teacher for each selected class.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="staff-assign-classes-popup">
          {classes.length === 0 ? <div className="text-xs text-slate-400 col-span-full py-4 text-center">No classes available</div> :
            classes.map((c) => {
              const label = `${c.name}${c.section ? "-" + c.section : ""}`;
              const checked = (f.assigned_classes || []).includes(c.id);
              return (
                <label key={c.id} className={`flex items-center gap-2 text-sm cursor-pointer rounded-md border px-2 py-1.5 hover:bg-slate-50 ${checked ? "border-indigo-400 bg-indigo-50" : "border-slate-200"}`} data-testid={`staff-class-opt-${c.id}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleClass(c.id)} />
                  <span className="truncate">{label}</span>
                </label>
              );
            })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setF((p) => ({ ...p, assigned_classes: [] }))} data-testid="staff-assign-clear-btn">Clear All</Button>
          <Button onClick={() => setClassDlgOpen(false)} data-testid="staff-assign-done-btn">Done ({(f.assigned_classes || []).length})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function Staff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [tab, setTab] = useState("Teaching");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("role");
  const [assignValue, setAssignValue] = useState("");
  const [assignClassId, setAssignClassId] = useState("");
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [classes, setClasses] = useState([]);
  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
  useEffect(() => { load(); api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);
  const canEdit = hasPerm(user, "edit_staff");
  const canManageUsers = hasPerm(user, "manage_users");
  const nav = useNavigate();

  const openNew = () => { setEdit(null); setOpen(true); };
  const openEdit = (s) => { setEdit(s); setOpen(true); };
  const toggleStatus = async (s) => {
    const next = s.status === "Disabled" ? "Active" : "Disabled";
    if (next === "Disabled" && !window.confirm(`Deactivate ${s.name}? They will no longer be able to log in.`)) return;
    try { await api.put(`/staff/${s.id}`, { status: next }); toast.success(next === "Active" ? "Staff activated" : "Staff deactivated"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeStaff = async (s) => {
    if (!window.confirm(`Move ${s.name} to Deleted Staff? You can restore them later from the Deleted Staff page.`)) return;
    try { await api.delete(`/staff/${s.id}`); toast.success("Staff deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkAction = async (action, value) => {
    const ids = [...selected];
    if (!ids.length) { toast.error("Select staff first"); return; }
    if (action === "delete" && !window.confirm(`Move ${ids.length} staff member(s) to Deleted Staff? You can restore them later.`)) return;
    try {
      const r = await api.post("/staff/bulk-action", { action, ids, value });
      toast.success(`${action === "delete" ? "Deleted" : "Updated"} ${r.data.affected} staff`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const doExport = async ({ mode, format, fields }) => {
    const body = { format, udise: mode === "udise", fields: mode === "selected" ? fields : [] };
    const r = await api.post("/staff/export", body, { responseType: "blob" });
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `staff-export.${ext}`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const doImport = async () => {
    if (!importFile) { toast.error("Choose a file to import"); return; }
    setImporting(true);
    const fd = new FormData(); fd.append("file", importFile);
    try {
      const r = await api.post("/staff/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} staff${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      setImportOpen(false); setImportFile(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const doSample = async (format) => {
    try {
      const r = await api.get("/staff/import-sample", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `staff_import_sample.${format === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download sample"); }
  };

  const openAssign = (mode) => {
    if (!selected.size) { toast.error("Select staff first"); return; }
    if (mode === "teacher" && selected.size !== 1) { toast.error("Select exactly one teacher to assign"); return; }
    setAssignMode(mode); setAssignValue(""); setAssignClassId("");
    if (mode === "role") api.get("/staff-roles").then((r) => setRoles(r.data.roles || DEFAULT_ROLES)).catch(() => {});
    if (mode === "teacher") api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    setAssignOpen(true);
  };

  const applyAssign = async () => {
    if (assignMode === "role") {
      if (!assignValue) { toast.error("Pick a role"); return; }
      await bulkAction("set_role", assignValue); setAssignOpen(false);
    } else if (assignMode === "type") {
      if (!assignValue) { toast.error("Pick a staff type"); return; }
      await bulkAction("set_staff_type", assignValue); setAssignOpen(false);
    } else {
      if (!assignClassId) { toast.error("Pick a class"); return; }
      const cls = classes.find((c) => c.id === assignClassId);
      const member = staff.find((s) => selected.has(s.id));
      try {
        await api.put(`/classes/${cls.id}`, { name: cls.name, section: cls.section || "", class_teacher: member.name, academic_session: cls.academic_session, room: cls.room || "", capacity: cls.capacity || 0, status: cls.status || "Active" });
        toast.success(`${member.name} set as class teacher of ${cls.name}${cls.section ? "-" + cls.section : ""}`);
        setAssignOpen(false); setSelected(new Set());
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    }
  };

  if (!staff) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const typeOf = (s) => (s.staff_type === "Non-Teaching" ? "Non-Teaching" : "Teaching");
  const classMap = Object.fromEntries(classes.map((c) => [c.id, `${c.name}${c.section ? "-" + c.section : ""}`]));
  const teachingCount = staff.filter((s) => typeOf(s) === "Teaching").length;
  const nonTeachingCount = staff.filter((s) => typeOf(s) === "Non-Teaching").length;
  const roleOptions = [...new Set(staff.map((s) => s.designation).filter(Boolean))].sort();
  const q = query.trim().toLowerCase();
  const filtered = staff
    .filter((s) => typeOf(s) === tab)
    .filter((s) => roleFilter === "all" || (s.designation || "") === roleFilter)
    .filter((s) => statusFilter === "all" || (s.status || "Active") === statusFilter)
    .filter((s) => !q || `${s.name} ${s.username} ${s.phone || ""} ${s.email || ""} ${s.designation || ""}`.toLowerCase().includes(q));
  const selectableIds = filtered.filter((s) => s.role !== "admin").map((s) => s.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));
  const selectedCount = selected.size;
  const colCount = 11 + (canEdit ? 1 : 0) + ((canEdit || canManageUsers) ? 1 : 0);

  const TabBtn = ({ value, label, count }) => (
    <button
      type="button"
      onClick={() => setTab(value)}
      className={`relative px-4 py-2.5 text-sm font-semibold transition-colors ${tab === value ? "text-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
      data-testid={`staff-tab-${value.toLowerCase().replace(/\s/g, "-")}`}
    >
      {label}
      <span className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${tab === value ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-500"}`}>{count}</span>
      {tab === value && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-indigo-600 rounded-full" />}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-3xl font-extrabold text-slate-900">Staff</h1><p className="text-slate-500">{staff.length} members</p></div>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="staff-tabs">
        <TabBtn value="Teaching" label="Teaching" count={teachingCount} />
        <TabBtn value="Non-Teaching" label="Non-Teaching" count={nonTeachingCount} />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search staff..." className="pl-9" data-testid="staff-search" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-36" data-testid="staff-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roleOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32" data-testid="staff-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {selectedCount > 0 && <span className="text-xs font-medium text-slate-500 mr-1" data-testid="staff-selected-count">{selectedCount} selected</span>}
            <ToolBtn icon={Plus} label="Add Staff" onClick={openNew} testid="add-staff-btn" />
            <ToolBtn icon={UploadCloud} label="Import staff (CSV / Excel)" onClick={() => setImportOpen(true)} testid="staff-import-btn" />
            <button type="button" title="Export staff" onClick={() => setExportOpen(true)} className="h-10 w-10 rounded-xl text-white bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-colors" data-testid="staff-export-btn"><DownloadCloud size={18} /></button>
            <ExportDialog open={exportOpen} onOpenChange={setExportOpen} entityLabel="staff" fieldsUrl="/staff/export-fields" onExport={doExport} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" title="Activate / Deactivate selected" disabled={!selectedCount} className="h-10 w-10 rounded-xl text-white bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="staff-access-btn"><UserCog size={18} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => bulkAction("activate")} data-testid="staff-bulk-activate"><CheckCircle2 size={14} className="mr-2 text-emerald-600" />Activate selected</DropdownMenuItem>
                <DropdownMenuItem onClick={() => bulkAction("deactivate")} data-testid="staff-bulk-deactivate"><Ban size={14} className="mr-2 text-rose-600" />Deactivate selected</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" title="Assign role / staff type / class teacher" disabled={!selectedCount} className="h-10 w-10 rounded-xl text-white bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="staff-assign-btn"><ClipboardList size={18} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openAssign("role")} data-testid="staff-assign-role">Set Role…</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openAssign("type")} data-testid="staff-assign-type">Set Staff Type…</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openAssign("teacher")} data-testid="staff-assign-teacher">Assign as Class Teacher…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ToolBtn icon={CheckSquare} label={allSelected ? "Clear selection" : "Select all"} onClick={toggleSelectAll} testid="staff-selectall-btn" />
            {canManageUsers && <ToolBtn icon={Trash2} label="Delete selected" variant="red" disabled={!selectedCount} onClick={() => bulkAction("delete")} testid="staff-bulk-delete-btn" />}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              {canEdit && <th className="px-3 py-3 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="staff-select-all" /></th>}
              <th className="text-left px-3 py-3 w-12">Se. No.</th>
              <th className="text-left px-4 py-3">Staff</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Designation</th>
              <th className="text-left px-4 py-3">Father's Name</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Assigned Classes</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Login</th>
              <th className="text-left px-4 py-3">Qualification</th>
              <th className="text-left px-4 py-3">Status</th>
              {(canEdit || canManageUsers) && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((s, i) => {
              const disabled = s.status === "Disabled";
              return (
                <tr key={s.id} className={disabled ? "opacity-60" : ""} data-testid={`staff-${s.username}`}>
                  {canEdit && <td className="px-3 py-3">{s.role !== "admin" && <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleOne(s.id)} data-testid={`staff-check-${s.username}`} />}</td>}
                  <td className="px-3 py-3 text-slate-500 tabular">{i + 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-indigo-600 text-white overflow-hidden flex items-center justify-center text-xs font-semibold shrink-0">{s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}</div>
                      <button onClick={() => nav(`/staff/${s.id}`)} className="font-medium text-slate-900 hover:text-indigo-600 text-left" data-testid={`staff-name-${s.username}`}>{s.name}</button>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${roleColor[s.role] || "bg-slate-100 text-slate-600"}`}>{s.role}</span></td>
                  <td className="px-4 py-3 text-slate-600">{s.designation || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{s.father_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{s.phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[220px]">{(s.assigned_classes || []).map((id) => classMap[id]).filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]">{s.email || "—"}</td>
                  <td className="px-4 py-3"><LoginCell username={s.username} password={s.password_plain} /></td>
                  <td className="px-4 py-3 text-slate-600">{s.qualification || "—"}</td>
                  <td className="px-4 py-3">{disabled ? <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">Disabled</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>}</td>
                  {(canEdit || canManageUsers) && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {s.role !== "admin" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><button className="text-slate-400 hover:text-slate-600 p-1" data-testid={`staff-menu-${s.username}`}><MoreVertical size={18} /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit && <DropdownMenuItem onClick={() => openEdit(s)} data-testid={`edit-staff-${s.username}`}><Pencil size={14} className="mr-2" />Edit Details</DropdownMenuItem>}
                            {canEdit && <DropdownMenuItem onClick={() => toggleStatus(s)} className={disabled ? "text-emerald-600" : "text-rose-600"} data-testid={`toggle-staff-${s.username}`}>
                              {disabled ? <><CheckCircle2 size={14} className="mr-2" />Activate</> : <><Ban size={14} className="mr-2" />Deactivate</>}
                            </DropdownMenuItem>}
                            {canManageUsers && <DropdownMenuItem onClick={() => removeStaff(s)} className="text-rose-600" data-testid={`delete-staff-${s.username}`}><Trash2 size={14} className="mr-2" />Delete Staff</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                  )}
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={colCount} className="text-center py-8 text-slate-400">No {tab.toLowerCase()} staff found.</td></tr>}
          </tbody>
        </table>
      </div>

      <StaffForm open={open} onOpenChange={setOpen} edit={edit} onSaved={load} />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Staff</DialogTitle>
            <DialogDescription>Upload a CSV or Excel (.xlsx) file. The first row must be column headers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Supported columns: <b>Name</b> (required), Username, Password, Access Level (staff/accountant), Role, Staff Type (Teaching/Non-Teaching), Phone, Email, Qualification, Salary.</p>
            <p className="text-xs text-slate-400">Missing usernames are auto-generated; missing passwords default to <code className="bg-slate-100 px-1 rounded">changeme123</code>.</p>
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <span className="text-xs text-slate-500">Not sure of the format?</span>
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => doSample("csv")} data-testid="staff-sample-csv"><DownloadCloud size={13} />Sample CSV</Button>
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => doSample("xlsx")} data-testid="staff-sample-xlsx"><DownloadCloud size={13} />Sample Excel</Button>
            </div>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="staff-import-file" className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-indigo-600 file:font-medium" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={doImport} disabled={importing} data-testid="staff-import-submit">{importing ? <Loader2 size={16} className="animate-spin mr-1" /> : <UploadCloud size={16} className="mr-1" />}Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{assignMode === "role" ? "Set Role" : assignMode === "type" ? "Set Staff Type" : "Assign as Class Teacher"}</DialogTitle>
            <DialogDescription>{assignMode === "teacher" ? "Assign the selected teacher to a class." : `Applies to ${selectedCount} selected staff.`}</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            {assignMode === "role" && (
              <Select value={assignValue} onValueChange={setAssignValue}>
                <SelectTrigger data-testid="assign-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {assignMode === "type" && (
              <Select value={assignValue} onValueChange={setAssignValue}>
                <SelectTrigger data-testid="assign-type-select"><SelectValue placeholder="Select staff type" /></SelectTrigger>
                <SelectContent><SelectItem value="Teaching">Teaching</SelectItem><SelectItem value="Non-Teaching">Non-Teaching</SelectItem></SelectContent>
              </Select>
            )}
            {assignMode === "teacher" && (
              <Select value={assignClassId} onValueChange={setAssignClassId}>
                <SelectTrigger data-testid="assign-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={applyAssign} data-testid="assign-apply-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
